import { useCallback, useEffect, useState } from 'react';

import {
  bootstrapShopify,
  enqueueHealthCheck,
  enqueueShopifySync,
  getLatestHealthReport,
  getShopifyCapabilities,
  getShopifySyncStatus,
  type BootstrapResult,
  type HealthResult,
  type SyncStatusResult,
} from './api';
import { BillingPanel } from './billing/BillingPanel';
import type { BillingModule } from './billing/billingApi';
import { CatalogSetupPanel } from './catalog/CatalogSetupPanel';
import { PolicyBuilder } from './commerce/PolicyBuilder';
import { ImpactGate } from './impact/ImpactGate';
import { PreviewPanel } from './preview/PreviewPanel';
import { PublishReadiness } from './publish/PublishReadiness';
import { RegistrationPanel } from './registration/RegistrationPanel';

const moduleNames: Record<string, string> = {
  commerce: 'B2B Commerce',
  buyer_experience: 'Buyer Experience',
  sales: 'Sales',
  order_desk: 'Order Desk',
  finance: 'Finance',
  intelligence: 'Intelligence & Automation',
};

type ConnectionState =
  | { status: 'connecting' }
  | { status: 'connected'; result: BootstrapResult }
  | { status: 'error'; message: string };

function syncDescription(
  status: SyncStatusResult | null,
  queueing: boolean,
  error: string | null,
): string {
  if (error) return `Sync needs attention: ${error}`;
  if (queueing) return 'Queueing Shopify synchronization…';
  if (status?.queue?.status === 'queued') return 'Synchronization is queued for the remote worker.';
  if (status?.queue?.status === 'running') return 'Remote synchronization is running.';
  if (!status?.sync) return 'No Shopify synchronization has completed yet.';
  if (status.sync.status === 'failed') {
    return `Last synchronization failed: ${status.sync.error ?? 'Unknown error'}`;
  }
  if (status.sync.status === 'completed') {
    return `Last synchronization completed with ${status.sync.records_seen.toLocaleString()} records processed.`;
  }
  return `Last synchronization status: ${status.sync.status}.`;
}

function healthStatusLabel(value: string): string {
  return value.replaceAll('_', ' ');
}

function summaryNumber(health: HealthResult | null, key: string): number {
  const value = health?.report?.summary[key];
  return typeof value === 'number' ? value : 0;
}

function scoreTrend(delta: number | null | undefined): string | null {
  if (delta === null || delta === undefined) return null;
  if (delta > 0) return `Improved ${delta} points since the previous check.`;
  if (delta < 0) return `Dropped ${Math.abs(delta)} points since the previous check.`;
  return 'No score change since the previous check.';
}

export function AppV12() {
  const [connection, setConnection] = useState<ConnectionState>({ status: 'connecting' });
  const [syncStatus, setSyncStatus] = useState<SyncStatusResult | null>(null);
  const [syncQueueing, setSyncQueueing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [capabilityCount, setCapabilityCount] = useState<number | null>(null);
  const [health, setHealth] = useState<HealthResult | null>(null);
  const [healthQueueing, setHealthQueueing] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [commerceEnabled, setCommerceEnabled] = useState<boolean | null>(null);

  const refreshDiagnostics = useCallback(async () => {
    const [syncResult, capabilityResult, healthResult] = await Promise.all([
      getShopifySyncStatus(),
      getShopifyCapabilities(),
      getLatestHealthReport(),
    ]);
    setSyncStatus(syncResult);
    setSyncError(null);
    setHealth(healthResult);

    if (capabilityResult.capabilities) {
      const supported = Object.entries(capabilityResult.capabilities).filter(
        ([key, value]) => key !== 'diagnostics' && value === true,
      ).length;
      setCapabilityCount(supported);
    } else {
      setCapabilityCount(null);
    }
  }, []);

  useEffect(() => {
    let active = true;
    bootstrapShopify()
      .then(async (result) => {
        if (!active) return;
        setConnection({ status: 'connected', result });
        try {
          await refreshDiagnostics();
        } catch {
          // A new install can be connected before its first sync exists.
        }
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Shopify connection failed.';
        setConnection({ status: 'error', message });
      });
    return () => {
      active = false;
    };
  }, [refreshDiagnostics]);

  useEffect(() => {
    if (connection.status !== 'connected') return undefined;
    const timer = window.setInterval(() => {
      void refreshDiagnostics().catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [connection.status, refreshDiagnostics]);

  const startSync = useCallback(async () => {
    setSyncQueueing(true);
    setSyncError(null);
    try {
      await enqueueShopifySync();
      setSyncStatus(await getShopifySyncStatus());
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Could not queue Shopify sync.';
      setSyncError(message);
    } finally {
      setSyncQueueing(false);
    }
  }, []);

  const startHealthCheck = useCallback(async () => {
    setHealthQueueing(true);
    setHealthError(null);
    try {
      await enqueueHealthCheck();
      setHealth(await getLatestHealthReport());
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Could not queue B2B Health Check.';
      setHealthError(message);
    } finally {
      setHealthQueueing(false);
    }
  }, []);

  const updateModuleAccess = useCallback((items: BillingModule[]) => {
    const commerce = items.find((item) => item.code === 'commerce');
    setCommerceEnabled(Boolean(commerce?.enabled));
  }, []);

  const syncBusy = syncQueueing || Boolean(syncStatus?.queue);
  const healthBusy = healthQueueing || Boolean(health?.queue);
  const report = health?.report ?? null;
  const trend = scoreTrend(health?.score_delta);
  const topRecommendation = report?.recommended_modules[0] ?? null;

  return (
    <s-page heading="Atellius B2B">
      <s-section heading="B2B operating layer for Shopify">
        <s-paragraph>
          Atellius configures, validates, explains, monitors, and progressively automates B2B
          operations while Shopify remains the commerce system of record.
        </s-paragraph>
      </s-section>

      <s-section heading="Shopify connection">
        {connection.status === 'connecting' && (
          <s-paragraph>Securely connecting this Shopify store to Atellius B2B…</s-paragraph>
        )}
        {connection.status === 'connected' && (
          <s-stack direction="block" gap="small">
            <s-heading>{connection.result.shop.name ?? connection.result.shop.domain}</s-heading>
            <s-paragraph>
              Connected as {connection.result.shop.domain}. Atellius Core is active for this store.
            </s-paragraph>
            <s-paragraph>{syncDescription(syncStatus, syncQueueing, syncError)}</s-paragraph>
            {capabilityCount !== null && (
              <s-paragraph>{capabilityCount} Shopify B2B capability groups detected.</s-paragraph>
            )}
            <s-button onClick={() => void startSync()} disabled={syncBusy}>
              {syncBusy ? 'Sync in progress' : 'Sync Shopify'}
            </s-button>
          </s-stack>
        )}
        {connection.status === 'error' && (
          <s-paragraph>Connection needs attention: {connection.message}</s-paragraph>
        )}
      </s-section>

      <s-section heading="B2B Health Check">
        {!health?.ready && !report && (
          <s-paragraph>
            Complete the first Shopify synchronization to unlock the free B2B Health Check.
          </s-paragraph>
        )}

        {health?.ready && !report && (
          <s-stack direction="block" gap="small">
            <s-paragraph>
              Shopify data is ready. Atellius will analyze the observable B2B configuration and
              identify issues without changing the store.
            </s-paragraph>
            {healthBusy && <s-paragraph>The remote Health Check is running…</s-paragraph>}
            <s-button onClick={() => void startHealthCheck()} disabled={healthBusy}>
              {healthBusy ? 'Health Check in progress' : 'Run B2B Health Check'}
            </s-button>
          </s-stack>
        )}

        {report && (
          <s-stack direction="block" gap="small">
            <s-heading>{report.score}/100</s-heading>
            <s-paragraph>B2B health: {healthStatusLabel(report.status)}</s-paragraph>
            {!health?.report_current && (
              <s-paragraph>
                {healthBusy
                  ? 'This score is from the previous snapshot. A fresh Health Check is running.'
                  : health?.ready
                    ? 'This score predates the latest Shopify sync. Run the Health Check to refresh it.'
                    : 'This score is from an earlier successful sync. Restore Shopify sync before relying on it.'}
              </s-paragraph>
            )}
            {trend && <s-paragraph>{trend}</s-paragraph>}
            {healthBusy && health?.report_current && (
              <s-paragraph>A fresh Health Check is running in the cloud.</s-paragraph>
            )}
            <s-paragraph>
              {summaryNumber(health, 'companies')} companies ·{' '}
              {summaryNumber(health, 'company_locations')} locations ·{' '}
              {summaryNumber(health, 'active_catalogs')} active catalogs ·{' '}
              {summaryNumber(health, 'products')} products
            </s-paragraph>
            <s-paragraph>
              {summaryNumber(health, 'critical')} critical · {summaryNumber(health, 'warning')}{' '}
              warnings · {summaryNumber(health, 'info')} informational
            </s-paragraph>

            {topRecommendation && (
              <s-stack direction="block" gap="small">
                <s-heading>Recommended capability</s-heading>
                <s-paragraph>
                  {moduleNames[topRecommendation.code] ?? topRecommendation.code} is relevant to{' '}
                  {topRecommendation.finding_count} current findings.
                </s-paragraph>
                <s-paragraph>
                  {topRecommendation.critical_count} critical · {topRecommendation.warning_count}{' '}
                  warning · {topRecommendation.score_impact} scored points affected
                </s-paragraph>
              </s-stack>
            )}

            {report.findings.slice(0, 5).map((finding) => (
              <s-stack key={finding.check_code} direction="block" gap="small">
                <s-heading>{finding.title}</s-heading>
                <s-paragraph>
                  {finding.severity.toUpperCase()}
                  {finding.score_penalty > 0 ? ` · score impact -${finding.score_penalty}` : ''}
                  {finding.affected_count > 0 ? ` · ${finding.affected_count} affected` : ''}
                </s-paragraph>
                <s-paragraph>{finding.description}</s-paragraph>
                <s-paragraph>Recommended action: {finding.recommendation}</s-paragraph>
                {finding.module_code && (
                  <s-paragraph>
                    Relevant Atellius module:{' '}
                    {moduleNames[finding.module_code] ?? finding.module_code}
                  </s-paragraph>
                )}
              </s-stack>
            ))}

            {report.findings.length === 0 && (
              <s-paragraph>No configuration findings were detected by the current checks.</s-paragraph>
            )}
            <s-button
              onClick={() => void startHealthCheck()}
              disabled={healthBusy || !health?.ready}
            >
              {healthBusy ? 'Health Check in progress' : 'Run again'}
            </s-button>
          </s-stack>
        )}

        {healthError && <s-paragraph>Health Check needs attention: {healthError}</s-paragraph>}
      </s-section>

      {connection.status === 'connected' && (
        <BillingPanel onModulesChanged={updateModuleAccess} />
      )}

      {connection.status === 'connected' && commerceEnabled === false && (
        <s-section heading="B2B Commerce">
          <s-paragraph>
            Registration, pricing rules, buyer preview, impact analysis, and safe publishing unlock
            when the B2B Commerce module is enabled for this store.
          </s-paragraph>
        </s-section>
      )}

      {connection.status === 'connected' && commerceEnabled && (
        <>
          <CatalogSetupPanel />
          <RegistrationPanel />
          <PolicyBuilder storeCurrency={connection.result.shop.currency} />
          <PreviewPanel />
          <ImpactGate />
          <PublishReadiness />
        </>
      )}
    </s-page>
  );
}
